import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Briefcase, MapPin, Clock, Search, Sparkles, Loader2, Send, X, Plus, Users, CheckCircle2, Building2, Wifi } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Full-time", "Part-time", "Contract"];

export default function Careers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);
  const [apply, setApply] = useState(null);
  const [resume, setResume] = useState("");
  const [phone, setPhone] = useState("");
  const [cover, setCover] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [matchText, setMatchText] = useState("");
  const [matches, setMatches] = useState(null);
  const [matching, setMatching] = useState(false);
  const [mine, setMine] = useState([]);
  const isEmployer = user && ["admin", "merchant"].includes(user.role);
  const [showPost, setShowPost] = useState(false);
  const empty = { title: "", department: "General", location: "Kingston, Jamaica", type: "Full-time", remote: false, description: "", requirements: "", salary_range: "" };
  const [job, setJob] = useState(empty);
  const [posting, setPosting] = useState(false);
  const [applicantsFor, setApplicantsFor] = useState(null);
  const [applicants, setApplicants] = useState([]);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (type) params.type = type;
    api.get("/careers/jobs", { params }).then(({ data }) => { setJobs(data); setLoading(false); }).catch(() => {});
    if (user) api.get("/careers/my-applications").then(({ data }) => setMine(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [type, user]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [q]);

  const runMatch = async () => {
    if (!user) { toast.error("Please sign in to use AI job match"); navigate("/login"); return; }
    if (matchText.trim().length < 20) { toast.error("Tell us a bit more about your skills and experience"); return; }
    setMatching(true); setMatches(null);
    try { const { data } = await api.post("/careers/match", { resume_text: matchText }); setMatches(data.matches); }
    catch { toast.error("AI match is unavailable right now"); }
    setMatching(false);
  };

  const submitApply = async () => {
    if (!user) { toast.error("Please sign in to apply"); navigate("/login"); return; }
    if (resume.trim().length < 20) { toast.error("Add a short summary of your experience"); return; }
    setSubmitting(true);
    try {
      await api.post(`/careers/jobs/${apply.id}/apply`, { resume_text: resume, phone, cover_note: cover });
      toast.success("Application submitted!");
      setApply(null); setResume(""); setPhone(""); setCover(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply"); }
    setSubmitting(false);
  };

  const postJob = async () => {
    if (!job.title || !job.description) { toast.error("Title and description are required"); return; }
    setPosting(true);
    try { await api.post("/careers/jobs", job); toast.success("Job posted"); setShowPost(false); setJob(empty); load(); }
    catch { toast.error("Could not post job"); }
    setPosting(false);
  };

  const viewApplicants = async (j) => {
    setApplicantsFor(j); setApplicants([]);
    try { const { data } = await api.get(`/careers/jobs/${j.id}/applications`); setApplicants(data); }
    catch { toast.error("Could not load applicants"); }
  };

  const appliedIds = new Set(mine.map((m) => m.job_id));

  return (
    <div data-testid="careers-page">
      {/* Hero */}
      <div className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs font-semibold mb-4"><Briefcase size={14} className="text-gold" /> SHOP NOW Careers</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold max-w-3xl leading-tight">Build the future of Jamaican commerce</h1>
          <p className="text-white/70 mt-3 max-w-2xl text-base">Join the team taking Jamaican businesses to the world. Explore open roles — or let our AI match you to the perfect fit.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        {/* AI Job Match */}
        <div className="bg-gradient-to-br from-emerald-deep to-emerald text-white rounded-2xl p-6" data-testid="ai-match-panel">
          <div className="flex items-center gap-2 mb-1"><Sparkles size={20} className="text-gold" /><h2 className="font-display font-bold text-lg">AI Job Match</h2></div>
          <p className="text-sm text-white/80 mb-4">Paste your skills or a short bio and our Recruitment AI will rank the roles that fit you best.</p>
          <textarea value={matchText} onChange={(e) => setMatchText(e.target.value)} rows={3}
            placeholder="e.g. 5 years building React and Python apps, led a small team, love shipping product…"
            className="w-full rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 px-4 py-3 text-sm" data-testid="ai-match-input" />
          <button onClick={runMatch} disabled={matching} className="mt-3 flex items-center gap-2 bg-gold text-ink font-semibold rounded-xl px-5 py-2.5 text-sm disabled:opacity-60" data-testid="ai-match-btn">
            {matching ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Find my matches
          </button>
          {matches && (
            <div className="mt-4 space-y-2" data-testid="ai-match-results">
              {matches.length === 0 ? <p className="text-sm text-white/80">No strong matches yet — try adding more detail about your experience.</p> :
                matches.map((m) => (
                  <div key={m.job_id} className="bg-white/10 rounded-xl p-3 flex items-start gap-3" data-testid={`match-${m.job_id}`}>
                    <div className="shrink-0 h-10 w-10 grid place-items-center rounded-lg bg-gold text-ink font-bold text-sm">{m.score}</div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">{m.title} <span className="text-white/60 font-normal">· {m.type} · {m.location}</span></div>
                      <div className="text-xs text-white/80">{m.reason}</div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Employer: post a job */}
        {isEmployer && (
          <div className="bg-card rounded-2xl border border-border p-6" data-testid="employer-panel">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Building2 size={18} className="text-emerald" /><h2 className="font-display font-bold">Employer Tools</h2></div>
              <button onClick={() => setShowPost((v) => !v)} className="flex items-center gap-1.5 bg-ink text-white rounded-xl px-4 py-2 text-sm font-semibold" data-testid="post-job-toggle"><Plus size={15} /> Post a Job</button>
            </div>
            {showPost && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="post-job-form">
                <input value={job.title} onChange={(e) => setJob((j) => ({ ...j, title: e.target.value }))} placeholder="Job title" className="rounded-xl border border-border px-3 py-2.5 text-sm sm:col-span-2" data-testid="job-title" />
                <input value={job.department} onChange={(e) => setJob((j) => ({ ...j, department: e.target.value }))} placeholder="Department" className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="job-department" />
                <input value={job.location} onChange={(e) => setJob((j) => ({ ...j, location: e.target.value }))} placeholder="Location" className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="job-location" />
                <select value={job.type} onChange={(e) => setJob((j) => ({ ...j, type: e.target.value }))} className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="job-type">
                  {TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <input value={job.salary_range} onChange={(e) => setJob((j) => ({ ...j, salary_range: e.target.value }))} placeholder="Salary range (optional)" className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="job-salary" />
                <textarea value={job.description} onChange={(e) => setJob((j) => ({ ...j, description: e.target.value }))} placeholder="Role description" rows={3} className="rounded-xl border border-border px-3 py-2.5 text-sm sm:col-span-2" data-testid="job-description" />
                <textarea value={job.requirements} onChange={(e) => setJob((j) => ({ ...j, requirements: e.target.value }))} placeholder="Requirements" rows={2} className="rounded-xl border border-border px-3 py-2.5 text-sm sm:col-span-2" data-testid="job-requirements" />
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={job.remote} onChange={(e) => setJob((j) => ({ ...j, remote: e.target.checked }))} /> Remote-friendly</label>
                <div className="sm:col-span-2">
                  <button onClick={postJob} disabled={posting} className="flex items-center gap-2 bg-emerald text-white rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="job-post-btn">{posting ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Publish Job</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div>
          <div className="flex flex-wrap gap-2 mb-5">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search roles" className="w-full rounded-xl border border-border pl-9 pr-3 py-2.5 text-sm" data-testid="careers-search" />
            </div>
            <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="careers-type-filter">
              <option value="">All types</option>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-emerald" size={28} /></div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="jobs-list">
              {jobs.map((j) => (
                <div key={j.id} className="bg-card rounded-2xl border border-border p-5 flex flex-col" data-testid={`job-card-${j.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display font-bold text-lg">{j.title}</h3>
                      <p className="text-xs text-emerald font-semibold mt-0.5">{j.employer} · {j.department}</p>
                    </div>
                    <span className="shrink-0 text-[11px] font-semibold bg-secondary rounded-full px-2.5 py-1">{j.type}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{j.description}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin size={13} /> {j.location}</span>
                    {j.remote && <span className="flex items-center gap-1 text-emerald"><Wifi size={13} /> Remote</span>}
                    {j.salary_range && <span className="flex items-center gap-1"><Clock size={13} /> {j.salary_range}</span>}
                  </div>
                  <div className="mt-4 pt-4 border-t border-border flex items-center gap-2">
                    {appliedIds.has(j.id) ? (
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald" data-testid={`applied-${j.id}`}><CheckCircle2 size={16} /> Applied</span>
                    ) : (
                      <button onClick={() => setApply(j)} className="flex-1 bg-ink text-white rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid={`apply-btn-${j.id}`}>Apply now</button>
                    )}
                    {isEmployer && (
                      <button onClick={() => viewApplicants(j)} className="flex items-center gap-1.5 border border-border rounded-xl px-3 py-2.5 text-sm font-semibold" data-testid={`applicants-btn-${j.id}`}><Users size={15} /> {j.applicants}</button>
                    )}
                  </div>
                </div>
              ))}
              {jobs.length === 0 && <p className="text-sm text-muted-foreground py-10 text-center md:col-span-2">No roles match your search right now.</p>}
            </div>
          )}
        </div>

        {/* My applications */}
        {user && mine.length > 0 && (
          <div className="bg-card rounded-2xl border border-border p-6" data-testid="my-applications">
            <h2 className="font-display font-bold mb-3">My Applications</h2>
            <div className="divide-y divide-border">
              {mine.map((m) => (
                <div key={m.id} className="py-3 flex items-center justify-between">
                  <span className="text-sm font-medium">{m.job_title}</span>
                  <span className="text-xs font-semibold bg-emerald/10 text-emerald rounded-full px-2.5 py-1 capitalize">{m.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Apply modal */}
      {apply && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="apply-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setApply(null)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-1"><h3 className="font-display font-bold text-lg">Apply · {apply.title}</h3><button onClick={() => setApply(null)} data-testid="apply-close"><X size={20} /></button></div>
            <p className="text-xs text-muted-foreground mb-4">{apply.employer} · {apply.location}</p>
            <label className="text-sm font-semibold">Your experience / resume summary</label>
            <textarea value={resume} onChange={(e) => setResume(e.target.value)} rows={5} placeholder="Summarise your skills, experience and why you're a great fit…" className="w-full rounded-xl border border-border px-3 py-2.5 text-sm mt-1" data-testid="apply-resume" />
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="apply-phone" />
            </div>
            <textarea value={cover} onChange={(e) => setCover(e.target.value)} rows={2} placeholder="Short cover note (optional)" className="w-full rounded-xl border border-border px-3 py-2.5 text-sm mt-3" data-testid="apply-cover" />
            <button onClick={submitApply} disabled={submitting} className="mt-4 w-full flex items-center justify-center gap-2 bg-ink text-white rounded-xl px-5 py-3 text-sm font-semibold disabled:opacity-60" data-testid="apply-submit">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Submit Application
            </button>
          </div>
        </div>
      )}

      {/* Applicants modal (employer) */}
      {applicantsFor && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="applicants-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setApplicantsFor(null)} />
          <div className="relative bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4"><h3 className="font-display font-bold text-lg">Applicants · {applicantsFor.title}</h3><button onClick={() => setApplicantsFor(null)} data-testid="applicants-close"><X size={20} /></button></div>
            {applicants.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No applications yet.</p> : (
              <div className="space-y-3">
                {applicants.map((a) => (
                  <div key={a.id} className="border border-border rounded-xl p-4">
                    <div className="flex items-center justify-between"><span className="font-semibold text-sm">{a.name}</span><span className="text-xs text-muted-foreground">{a.email}{a.phone ? ` · ${a.phone}` : ""}</span></div>
                    <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{a.resume_text}</p>
                    {a.cover_note && <p className="text-xs italic text-muted-foreground mt-2">"{a.cover_note}"</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
